import { useEffect, useMemo, useState } from "react";
import { ScrollText } from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import { api } from "../../../services/apiService";
import { DataTable } from "../../../components/ops/DataTable";
import { DataToolbar } from "../../../components/ops/DataToolbar";
import { EmptyState } from "../../../components/ops/EmptyState";
import { PageHeader } from "../../../components/ops/PageHeader";
import { Pagination } from "../../../components/ops/Pagination";
import { usePagedList } from "../../../components/ops/usePagedList";

const actionLabels = {
  "user.role_changed": "Changed user role",
  "membership_plan.created": "Created membership plan",
  "membership_plan.updated": "Updated membership plan",
  "enrollment.status_changed": "Changed enrollment status",
  "enrollment.cancelled": "Cancelled enrollment",
  "booking.cancelled": "Cancelled booking",
  "program.updated": "Updated program",
  "lead.status_changed": "Changed lead status",
};

export function AdminAuditLogPage() {
  const { accessToken } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [entityFilter, setEntityFilter] = useState("all");

  useEffect(() => {
    setLoading(true);
    api.get("/admin/audit-log")
      .then((response) => setEntries(response.data.entries || []))
      .catch(() => setError("Unable to load the audit log"))
      .finally(() => setLoading(false));
  }, [accessToken]);

  const entityOptions = useMemo(() => {
    const unique = [...new Set(entries.map((entry) => entry.entity))].sort();
    return [{ value: "all", label: "All entities" }, ...unique.map((entity) => ({ value: entity, label: entity }))];
  }, [entries]);

  const filteredEntries = useMemo(() => {
    return entries.filter((entry) => (entityFilter === "all" ? true : entry.entity === entityFilter));
  }, [entries, entityFilter]);

  const { page, setPage, totalPages, totalItems, pageItems } = usePagedList(filteredEntries, {
    search,
    searchFields: ["actor.firstName", "actor.lastName", "action", "entity"],
    pageSize: 12,
  });

  const columns = [
    { key: "when", label: "When", render: (row) => new Date(row.createdAt).toLocaleString() },
    { key: "actor", label: "Actor", render: (row) => (row.actor ? `${row.actor.firstName} ${row.actor.lastName}` : "—") },
    { key: "action", label: "Action", render: (row) => actionLabels[row.action] || row.action },
    { key: "entity", label: "Entity", render: (row) => `${row.entity}${row.entityId ? ` (${String(row.entityId).slice(-6)})` : ""}` },
  ];

  return (
    <div>
      <PageHeader title="Audit Log" subtitle="A record of sensitive changes made across the admin console" />

      <div className="ops-card">
        {error && <div className="ops-alert ops-alert--error">{error}</div>}
        <DataToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search by actor, action, or entity..."
          filters={[{ label: "Entity", value: entityFilter, onChange: setEntityFilter, options: entityOptions }]}
          resultCount={totalItems}
          itemLabel="entries"
        />
        {loading ? (
          <p className="ops-loading">Loading audit log…</p>
        ) : totalItems === 0 ? (
          <EmptyState icon={ScrollText} title="No activity found" description={entries.length === 0 ? "Sensitive actions like role changes and cancellations will be recorded here." : "Try adjusting your search or filters."} />
        ) : (
          <>
            <DataTable columns={columns} rows={pageItems} rowKey={(row) => row._id} />
            <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
          </>
        )}
      </div>
    </div>
  );
}
